// src/components/public/StoriesCircle.js
import React from 'react';
import { Box, Avatar, Badge, Typography, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';

// Color constante para el borde de historias
const STORIES_GRADIENT = 'linear-gradient(45deg, #4CAF50, #2E7D32)';

// Badge estilizado para indicar nuevas historias
const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const StoriesCircle = ({ 
  image, 
  name, 
  hasStories = false, 
  isViewed = false, 
  size = 'medium',
  onClick,
  loading = false
}) => {
  // Determinar tamaños según la propiedad size
  const getSizes = () => {
    switch (size) {
      case 'small':
        return { 
          avatar: 50, 
          ringSize: 56, 
          ringThickness: 2,
          fontSize: '0.7rem',
          badgeSize: 8
        };
      case 'large':
        return { 
          avatar: 80, 
          ringSize: 88, 
          ringThickness: 3,
          fontSize: '0.9rem',
          badgeSize: 12
        };
      case 'medium':
      default:
        return { 
          avatar: 64, 
          ringSize: 72, 
          ringThickness: 2.5,
          fontSize: '0.8rem',
          badgeSize: 10
        };
    }
  };
  
  const { avatar, ringSize, ringThickness, fontSize, badgeSize } = getSizes();
  
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        justifyContent: 'center',
        width: ringSize,
        mx: 0.5
      }}>
        <Skeleton 
          variant="circular" 
          width={ringSize} 
          height={ringSize} 
          animation="wave"
        />
        <Skeleton 
          variant="text" 
          width={ringSize} 
          height={20} 
          animation="wave" 
          sx={{ mt: 0.5 }} 
        />
      </Box>
    );
  }
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        cursor: hasStories ? 'pointer' : 'default',
        mx: 0.5
      }}
      onClick={hasStories ? onClick : undefined}
    >
      {hasStories ? (
        <Box
          sx={{
            width: ringSize,
            height: ringSize,
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: isViewed ? 'rgba(0,0,0,0.1)' : STORIES_GRADIENT,
            p: 0.5
          }}
        >
          <StyledBadge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant={hasStories && !isViewed ? "dot" : "standard"}
            badgeContent=" "
            sx={{
              '& .MuiBadge-badge': {
                width: badgeSize,
                height: badgeSize,
                borderRadius: '50%'
              }
            }}
          >
            <Avatar
              src={image}
              alt={name}
              sx={{ 
                width: avatar, 
                height: avatar,
                border: '2px solid white'
              }}
            />
          </StyledBadge>
        </Box>
      ) : (
        <Avatar
          src={image}
          alt={name}
          sx={{ 
            width: avatar, 
            height: avatar
          }}
        />
      )}
      
      <Typography 
        variant="caption"
        align="center"
        sx={{ 
          mt: 0.5, 
          fontSize,
          maxWidth: ringSize,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {name}
      </Typography>
    </Box>
  );
};

export default StoriesCircle;